/**
 * PSV-1337 — client-side asteroid rendering.
 *
 * Server is authoritative for asteroid lifecycle; the client subscribes
 * to `AsteroidSpawned / AsteroidUpdated / AsteroidMoved / AsteroidDestroyed`
 * events and reflects them on screen. Visual responsibilities:
 *   - Sprite (one of three textures, picked from `radius` proxy for tier)
 *   - Bonus glow ring (alpha-pulsed, color by bonus type)
 *   - Small HP bar above the asteroid (visible while damaged)
 *   - Smooth position lerp between 10Hz `AsteroidMoved` broadcasts
 *   - Destruction particle burst + "+N gems" / "+BONUS!" fly-up text
 *
 * Lerp window is 100ms — matches the server's `ASTEROID_TICKS_PER_BROADCAST`
 * = 6 → 10Hz cadence. If a frame comes in late, the lerp simply caps at
 * `interpEnd` and waits for the next push.
 */

import Phaser from 'phaser';

const HP_BAR_WIDTH = 30;
const HP_BAR_HEIGHT = 3;
const HP_BAR_OFFSET = 8; // px above sprite top
// PSV-1902 — the lerp window ADAPTS to the actual gap between motion
// frames (event delivery jitters hard under fire traffic; a fixed 100ms
// window stalled between late frames = visible stutter). Extrapolation
// keeps rocks drifting up to one extra window when a frame is late.
const INTERP_MIN_MS = 60;
const INTERP_MAX_MS = 300;
const INTERP_FALLBACK_MS = 100; // first frame for an entry
const EXTRAPOLATE_CAP = 2; // t is clamped here (one full window beyond)

// Bonus visual styling — keep tints distinct enough to read at a glance.
const BONUS_GLOW_COLORS: Record<string, number> = {
  heal: 0x44ff44,
  gemMult: 0xffdd44,
  damageBoost: 0xff5555,
  shield: 0x4488ff,
  speedBoost: 0x44ffff,
};
const BONUS_GLOW_LABELS: Record<string, string> = {
  heal: '+HEAL',
  gemMult: '+GEM x5',
  damageBoost: '+DMG',
  shield: '+SHIELD',
  speedBoost: '+SPEED',
};

const GEM_COLOR_STR = '#ffd84a';

interface AsteroidRenderEntry {
  id: string;
  sprite: Phaser.GameObjects.Sprite;
  hpBar: Phaser.GameObjects.Graphics;
  bonusGlow: Phaser.GameObjects.Graphics | null;
  bonusGlowTween: Phaser.Tweens.Tween | null;
  hp: number;
  hpMax: number;
  radius: number;
  bonusType: string;
  // Interpolation: server pushes at 10Hz; we lerp between two recent points.
  interpStartX: number;
  interpStartY: number;
  interpEndX: number;
  interpEndY: number;
  interpStartedAt: number; // ms
  interpDurationMs: number;
}

/**
 * Map `radius` (from server) to one of three texture keys. Mirrors the
 * server's tier table radii (15 / 25 / 40); anything between gets the
 * nearest tier sprite. Keep the texture key list aligned with the
 * preloads in `BootScene.preload`.
 */
function textureKeyForRadius(radius: number): string {
  if (radius >= 35) return 'asteroid_large';
  if (radius >= 20) return 'asteroid_medium';
  return 'asteroid_small';
}

export class AsteroidManager {
  private readonly scene: Phaser.Scene;
  private readonly entries = new Map<string, AsteroidRenderEntry>();

  /** PSV-1729 — radar feed: world positions + radii of live asteroids. */
  radarBlips(): Array<{ x: number; y: number; radius: number }> {
    return Array.from(this.entries.values(), (e) => ({
      x: e.sprite.x,
      y: e.sprite.y,
      radius: e.radius,
    }));
  }

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Server says a fresh asteroid exists — create the sprite + HUD bits. */
  spawnAsteroid(payload: {
    id: string;
    x: number;
    y: number;
    radius: number;
    hpMax: number;
    hp: number;
    bonusType: string;
  }): void {
    if (this.entries.has(payload.id)) return; // idempotent
    const texture = textureKeyForRadius(payload.radius);
    const sprite = this.scene.add.sprite(payload.x, payload.y, texture);
    sprite.setOrigin(0.5, 0.5);
    // Scale texture so its visible footprint matches the server-side
    // hit radius. Source PNGs are 1024×1024 with substantial padding,
    // so 2× the radius (= diameter) over the source width gives us the
    // intended visible diameter on screen.
    const src = this.scene.textures.get(texture);
    const srcWidth =
      src.source[0] !== undefined && src.source[0].width > 0
        ? src.source[0].width
        : payload.radius * 2;
    sprite.setScale((payload.radius * 2) / srcWidth);
    sprite.setDepth(-10); // above background, below ships (ships at depth 0)

    const hpBar = this.scene.add.graphics();
    hpBar.setDepth(-9);

    let bonusGlow: Phaser.GameObjects.Graphics | null = null;
    let bonusGlowTween: Phaser.Tweens.Tween | null = null;
    if (payload.bonusType !== '' && payload.bonusType in BONUS_GLOW_COLORS) {
      bonusGlow = this.scene.add.graphics();
      bonusGlow.setDepth(-11);
      const color = BONUS_GLOW_COLORS[payload.bonusType]!;
      // Pre-bake the ring; tween only its alpha so we don't redraw each
      // frame.
      bonusGlow.fillStyle(color, 0.25);
      bonusGlow.fillCircle(0, 0, payload.radius + 8);
      bonusGlow.fillStyle(color, 0.45);
      bonusGlow.fillCircle(0, 0, payload.radius + 4);
      bonusGlow.setPosition(payload.x, payload.y);
      bonusGlowTween = this.scene.tweens.add({
        targets: bonusGlow,
        alpha: { from: 0.6, to: 1 },
        duration: 700,
        yoyo: true,
        repeat: -1,
      });
    }

    const entry: AsteroidRenderEntry = {
      id: payload.id,
      sprite,
      hpBar,
      bonusGlow,
      bonusGlowTween,
      hp: payload.hp,
      hpMax: payload.hpMax,
      radius: payload.radius,
      bonusType: payload.bonusType,
      interpStartX: payload.x,
      interpStartY: payload.y,
      interpEndX: payload.x,
      interpEndY: payload.y,
      interpStartedAt: Date.now(),
      interpDurationMs: INTERP_FALLBACK_MS,
    };
    this.entries.set(payload.id, entry);
    this.redrawHpBar(entry);
  }

  /** Server reports new HP for an asteroid — refresh the bar. */
  updateAsteroidHp(id: string, hp: number): void {
    const entry = this.entries.get(id);
    if (entry === undefined) return;
    entry.hp = hp;
    this.redrawHpBar(entry);
  }

  /**
   * Server pushed a 10Hz motion update — set new interp target.
   * Position interpolates over INTERP_DURATION_MS in the per-frame update.
   */
  moveAsteroid(id: string, x: number, y: number): void {
    const entry = this.entries.get(id);
    if (entry === undefined) return;
    // Snap interpStart to current rendered position so the new lerp
    // begins cleanly even if the previous lerp didn't complete.
    entry.interpStartX = entry.sprite.x;
    entry.interpStartY = entry.sprite.y;
    entry.interpEndX = x;
    entry.interpEndY = y;
    const now = Date.now();
    // Adapt the window to the observed frame gap so jittery delivery
    // still animates smoothly (see INTERP_* comment).
    const gap = now - entry.interpStartedAt;
    entry.interpDurationMs = Math.min(INTERP_MAX_MS, Math.max(INTERP_MIN_MS, gap));
    entry.interpStartedAt = now;
  }

  /**
   * Server confirms destruction — spawn the particle burst + fly-up
   * "+N gems" (and "+BONUS!" if `bonusType !== ''`) tied to the shooter
   * being us. Caller passes `isOwnShot` because shooter-identity is
   * controller-level state (myUserId). Removes the entry + cleans up
   * sprites + tweens.
   */
  destroyAsteroid(
    id: string,
    x: number,
    y: number,
    gemValue: number,
    bonusType: string,
    isOwnShot: boolean,
  ): void {
    const entry = this.entries.get(id);
    if (entry !== undefined) {
      entry.sprite.destroy();
      entry.hpBar.destroy();
      if (entry.bonusGlowTween !== null) entry.bonusGlowTween.remove();
      if (entry.bonusGlow !== null) entry.bonusGlow.destroy();
      this.entries.delete(id);
    }

    this.spawnDebrisBurst(x, y);
    if (isOwnShot) {
      this.spawnFlyUpText(x, y, `+${gemValue} gems`, GEM_COLOR_STR);
      if (bonusType !== '' && bonusType in BONUS_GLOW_LABELS) {
        const label = BONUS_GLOW_LABELS[bonusType]!;
        const color = BONUS_GLOW_COLORS[bonusType] ?? 0xffffff;
        this.spawnFlyUpText(x, y - 22, label, hexToCssColor(color));
      }
    }
  }

  /** Called per Phaser frame from ArenaController.update(). */
  update(_deltaMs: number): void {
    const now = Date.now();
    for (const entry of this.entries.values()) {
      const t = Math.min(
        EXTRAPOLATE_CAP,
        (now - entry.interpStartedAt) / entry.interpDurationMs,
      );
      const x = entry.interpStartX + (entry.interpEndX - entry.interpStartX) * t;
      const y = entry.interpStartY + (entry.interpEndY - entry.interpStartY) * t;
      entry.sprite.setPosition(x, y);
      entry.hpBar.setPosition(x, y - entry.radius - HP_BAR_OFFSET);
      if (entry.bonusGlow !== null) {
        entry.bonusGlow.setPosition(x, y);
      }
    }
  }

  destroy(): void {
    for (const entry of this.entries.values()) {
      entry.sprite.destroy();
      entry.hpBar.destroy();
      if (entry.bonusGlowTween !== null) entry.bonusGlowTween.remove();
      if (entry.bonusGlow !== null) entry.bonusGlow.destroy();
    }
    this.entries.clear();
  }

  // ---------- private ----------

  private redrawHpBar(entry: AsteroidRenderEntry): void {
    const g = entry.hpBar;
    g.clear();
    // Hide bar when at full HP — keeps the field clean visually until
    // the asteroid actually takes damage.
    if (entry.hp >= entry.hpMax) return;
    if (entry.hp <= 0) return;
    const ratio = Math.max(0, Math.min(1, entry.hp / entry.hpMax));
    const halfW = HP_BAR_WIDTH / 2;
    g.fillStyle(0x1a1a1a, 0.8);
    g.fillRect(-halfW, 0, HP_BAR_WIDTH, HP_BAR_HEIGHT);
    const color =
      ratio > 0.6 ? 0x88ddff : ratio > 0.3 ? 0xffdd00 : 0xff5555;
    g.fillStyle(color, 1);
    g.fillRect(-halfW, 0, HP_BAR_WIDTH * ratio, HP_BAR_HEIGHT);
  }

  private spawnDebrisBurst(x: number, y: number): void {
    // 12-18 small gray-brown particles flung radially, fading out over
    // 400-700ms. Lighter than the ship death explosion since asteroids
    // pop several times per minute.
    const count = 12 + Math.floor(Math.random() * 7);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 100 + Math.random() * 200;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      const size = 2 + Math.floor(Math.random() * 3);
      const color = Math.random() < 0.5 ? 0x9a8866 : 0xccaa88;
      const lifetime = 400 + Math.random() * 300;
      const g = this.scene.add.graphics();
      g.fillStyle(color, 1);
      g.fillCircle(0, 0, size);
      g.setPosition(x, y);
      g.setDepth(3);
      this.scene.tweens.add({
        targets: g,
        x: x + vx * (lifetime / 1000),
        y: y + vy * (lifetime / 1000),
        alpha: 0,
        duration: lifetime,
        ease: 'Quad.easeOut',
        onComplete: () => g.destroy(),
      });
    }
  }

  private spawnFlyUpText(
    x: number,
    y: number,
    text: string,
    color: string,
  ): void {
    const t = this.scene.add
      .text(x, y, text, {
        fontSize: '16px',
        color,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(16);
    this.scene.tweens.add({
      targets: t,
      y: y - 40,
      alpha: 0,
      duration: 900,
      ease: 'Quad.easeOut',
      onComplete: () => t.destroy(),
    });
  }
}

function hexToCssColor(hex: number): string {
  return '#' + hex.toString(16).padStart(6, '0');
}
